import {
  createFocusOutlineStyle,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  AppBarWide,
  FlyoutTileHeight,
  FlyoutTileHeightMaxWidth480,
  FlyoutTileWidth,
  FlyoutTileWidthMaxWidth480,
  TeamsTokens,
  TileDimensionDefault,
  TileHeightMercury,
  TileHeightWide,
  TileMarginDefault,
  TileWidthMercury,
  VisualRefresh,
} from "./Constants";
const mercuryDesignHoverShadow = `2px 2px 3px 1px ${TeamsTokens.colorTeamsButtonCompositeHoverShadow1}, -2px 2px 3px 1px ${TeamsTokens.colorTeamsButtonCompositeHoverShadow2}, -2px 3px 3px 1px ${TeamsTokens.colorTeamsButtonCompositeHoverShadow3}, 2px 3px 3px 1px ${TeamsTokens.colorTeamsButtonCompositeHoverShadow4}`;
const mercuryDesignActiveShadow = `2px 2px 3px 1px ${TeamsTokens.colorTeamsButtonCompositeFocusShadow1}, -2px 2px 3px 1px ${TeamsTokens.colorTeamsButtonCompositeFocusShadow2}, -2px 3px 3px 1px ${TeamsTokens.colorTeamsButtonCompositeFocusShadow3}, 2px 3px 3px 1px ${TeamsTokens.colorTeamsButtonCompositeFocusShadow4}`;
const mercuryDesignOutlineShorthands = {
  ...shorthands.outline("1px", "solid", tokens.colorNeutralStrokeAlpha),
};
const outlineShorthandStyles = {
  ...shorthands.outline(
    tokens.strokeWidthThin,
    "solid",
    tokens.colorNeutralStrokeAlpha
  ),
  ...shorthands.borderRadius(VisualRefresh.tileBorderRadius),
};
const forcedColors = {
  "@media (forced-colors: active)": {
    forcedColorAdjust: "none",
    backgroundColor: "Highlight",
  },
};
// Define shared styles for dragOver and hover states, since
// no dragOver pseudo-class exists in CSS.
const buttonCommonTwoColumnViewHoverAndDragOver = {
  backgroundColor: tokens.colorNeutralBackground4Selected,
  ...forcedColors,
};
const buttonCommonThreeColumnViewHoverAndDragOver = {
  backgroundColor: tokens.colorNeutralBackground5Selected,
  ...forcedColors,
};
const micaButtonV2WebHoverAndDragOver = {
  backgroundColor: tokens.colorNeutralBackground5Hover,
  boxShadow: "none",
  ...shorthands.borderColor("transparent"),
  "& div": {
    "& > svg": {
      fill: tokens.colorNeutralForeground3BrandHover,
    },
  },
};
const micaButtonV2DesktopHoverAndDragOver = {
  backgroundColor: tokens.colorSubtleBackgroundLightAlphaHover,
  boxShadow: "none",
  ...shorthands.borderColor("transparent"),
  "& div": {
    "& > svg": {
      fill: tokens.colorNeutralForeground3BrandHover,
    },
  },
};
const mercuryTileHoverAndDragOver = {
  ...mercuryDesignOutlineShorthands,
  backgroundColor: tokens.colorNeutralBackground4Selected,
  boxShadow: mercuryDesignHoverShadow,
  ":active": {
    ...mercuryDesignOutlineShorthands,
    backgroundColor: tokens.colorNeutralBackground4Selected,
    boxShadow: mercuryDesignActiveShadow,
  },
};
const flyoutTileHoverAndDragOver = {
  backgroundColor: tokens.colorNeutralBackground4Selected,
  boxShadow:
    "0px 0.3px 0.9px rgba(0, 0, 0, 0.07), 0px 1.6px 3.6px rgba(0, 0, 0, 0.11)",
};
export const useLaunchTileStyles = makeStyles({
  tileFocusIndicator: createFocusOutlineStyle({
    selector: "focus",
    style: {
      outlineWidth: "2px",
      outlineOffset: "-2px",
      outlineColor: tokens.colorStrokeFocus2,
    },
  }),
  tileContainer: {
    position: "relative",
  },
  micaTileContainerV2Desktop: {
    position: "relative",
  },
  flyoutTileContainer: {
    position: "relative",
  },
  buttonCommonTwoColumnViewDragOver: buttonCommonTwoColumnViewHoverAndDragOver,
  buttonCommonThreeColumnViewDragOver:
    buttonCommonThreeColumnViewHoverAndDragOver,
  micaButtonV2WebDragOver: micaButtonV2WebHoverAndDragOver,
  micaButtonV2DesktopDragOver: micaButtonV2DesktopHoverAndDragOver,
  mercuryTileDragOver: mercuryTileHoverAndDragOver,
  flyoutTileDragOver: flyoutTileHoverAndDragOver,
  button: {
    ...shorthands.borderWidth(0),
    ...shorthands.padding("1px"),
    minWidth: TileDimensionDefault,
    maxWidth: TileDimensionDefault,
    height: TileDimensionDefault,
    width: TileDimensionDefault,
    marginTop: TileMarginDefault,
    marginBottom: TileMarginDefault,
    fontWeight: tokens.fontWeightRegular,
    backgroundColor: tokens.colorSubtleBackground,
    userSelect: "none",
    touchAction: "none",
  },
  buttonCommon: {
    ":hover": {
      boxShadow: "none",
    },
    ":focus-visible": {
      boxShadow: "none",
    },
  },
  buttonCommonTwoColumnView: {
    ":hover": buttonCommonTwoColumnViewHoverAndDragOver,
  },
  buttonCommonThreeColumnView: {
    ":hover": buttonCommonThreeColumnViewHoverAndDragOver,
  },
  micaButtonV2Web: {
    "&:hover": micaButtonV2WebHoverAndDragOver,
    "&:active:hover": {
      backgroundColor: tokens.colorNeutralBackground5Pressed,
      ...shorthands.borderColor("transparent"),
      "& div": {
        "& > svg": {
          fill: tokens.colorNeutralForeground3BrandPressed,
        },
      },
    },
    ":focus-visible": {
      boxShadow: "none",
      ...shorthands.borderColor("transparent"),
    },
  },
  micaButtonV2Desktop: {
    "&:hover": micaButtonV2DesktopHoverAndDragOver,
    "&:active:hover": {
      backgroundColor: tokens.colorSubtleBackgroundLightAlphaPressed,
      ...shorthands.borderColor("transparent"),
      "& div": {
        "& > svg": {
          fill: tokens.colorNeutralForeground3BrandPressed,
        },
      },
    },
    ":focus-visible": {
      boxShadow: "none",
      ...shorthands.borderColor("transparent"),
    },
  },
  mercuryDesign: {
    ...createFocusOutlineStyle({
      selector: "focus",
      style: {
        outlineWidth: "2px",
        outlineColor: tokens.colorStrokeFocus2,
        outlineRadius: "12px",
      },
    }),
    width: TileWidthMercury,
    maxWidth: TileWidthMercury,
    height: TileHeightMercury,
    maxHeight: TileHeightMercury,
    position: "relative",
    ...shorthands.borderRadius("12px"),
    ...shorthands.overflow("visible"),
    ...shorthands.margin("1px", "0px", "2px", "0px"),
    "&:hover": mercuryTileHoverAndDragOver,
    "&:active": {
      ...mercuryDesignOutlineShorthands,
      backgroundColor: tokens.colorNeutralBackground4Selected,
      boxShadow: mercuryDesignActiveShadow,
    },
  },
  selected: {
    backgroundColor: tokens.colorTransparentBackgroundSelected,
    ...forcedColors,
  },
  micaSelectedV2Web: {
    backgroundColor: tokens.colorTransparentBackgroundSelected,
  },
  micaSelectedV2Desktop: {
    backgroundColor: tokens.colorSubtleBackgroundLightAlphaSelected,
  },
  mercuryDesignSelected: {
    backgroundColor: tokens.colorSubtleBackgroundLightAlphaHover,
    ...mercuryDesignOutlineShorthands,
  },
  wideButton: {
    minWidth: AppBarWide,
    maxWidth: AppBarWide,
    width: AppBarWide,
    height: TileHeightWide,
    minHeight: TileHeightWide,
    marginTop: 0,
    marginBottom: 0,
    ...shorthands.borderRadius(0),
  },
  roundedTileButtonContainer: {
    display: "flex",
    height: VisualRefresh.tileContainerHeight,
    width: VisualRefresh.tileContainerWidth,
    justifyContent: "center",
    alignItems: "center",
  },
  roundedTileButton: {
    minWidth: VisualRefresh.tileMinWidth,
    maxWidth: VisualRefresh.tileMaxWidth,
    minHeight: VisualRefresh.tileMinHeight,
    width: VisualRefresh.tileWidth,
    height: VisualRefresh.tileHeight,
    ...shorthands.borderRadius(VisualRefresh.tileBorderRadius),
    ...shorthands.overflow("visible"),
    position: "relative",
    transition: "all 0.3s ease",
    "&:focus": {
      ...outlineShorthandStyles,
    },
    "&::before": {
      content: '""',
      position: "absolute",
      left: "-5px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "0",
      height: "0",
      backgroundColor: tokens.colorNeutralForeground3BrandSelected,
      borderRadius: "50%",
      transition: "width 0.3s ease, height 0.3s ease",
    },
    "&:hover::before": {
      width: "3px",
      height: "3px",
    },
    "&:active::before": {
      width: "3px",
      height: "16px",
      borderRadius: tokens.borderRadiusCircular,
    },
    "&.selected::before": {
      width: "3px",
      height: "16px",
      borderRadius: tokens.borderRadiusCircular,
      transition: "none",
    },
  },
  roundedTileButtonSelected: {
    backgroundColor: tokens.colorNeutralBackground4Selected,
    ...outlineShorthandStyles,
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "-5px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "3px",
      height: "16px",
      backgroundColor: tokens.colorNeutralForeground3BrandSelected,
      borderRadius: tokens.borderRadiusCircular,
      transition: "width 0.3s ease, height 0.3s ease",
    },
  },
  visualRefreshSelectionBar: {
    "&::before": {
      background: VisualRefresh.radialGradientColor,
    },
  },
  flyoutTile: {
    minWidth: FlyoutTileWidth,
    maxWidth: FlyoutTileWidth,
    minHeight: FlyoutTileHeight,
    ...shorthands.borderRadius("6px"),
    ":hover": flyoutTileHoverAndDragOver,
    ":focus-visible": {
      boxShadow:
        "0px 0.15px 0.45px rgba(0, 0, 0, 0.11), 0px 0.8px 1.8px rgba(0, 0, 0, 0.13)",
    },
    "&:active:hover": {
      backgroundColor: tokens.colorNeutralBackground4Selected,
      boxShadow:
        "0px 0.15px 0.45px rgba(0, 0, 0, 0.11), 0px 0.8px 1.8px rgba(0, 0, 0, 0.13)",
    },
    "@media (max-width: 480px)": {
      minWidth: FlyoutTileWidthMaxWidth480,
      maxWidth: FlyoutTileWidthMaxWidth480,
      minHeight: FlyoutTileHeightMaxWidth480,
    },
  },
  transparentBackground: {
    backgroundColor: tokens.colorTransparentBackground,
  },
  dragging: {
    opacity: 0.5,
    marginLeft: "2px",
    zIndex: 100,
  },
});
